import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Wrench, Package, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Header = () => {
    const { user, logout, isAdmin } = useAuth();
    const { itemCount } = useCart();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
                        <span className="text-2xl font-bold text-primary">G-Store</span>
                        <span className="hidden sm:block text-sm text-white tracking-widest">Assistenza e Riparazione</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8">
                        <Link to="/store" className="text-muted hover:text-white transition-colors font-medium" data-testid="nav-store">
                            Store
                        </Link>
                        <Link to="/riparazioni" className="text-muted hover:text-white transition-colors font-medium" data-testid="nav-repairs">
                            Riparazioni
                        </Link>
                        <Link to="/listino" className="text-muted hover:text-white transition-colors font-medium" data-testid="nav-pricelist">
                            Listino
                        </Link>
                        <Link to="/confronta" className="text-muted hover:text-white transition-colors font-medium" data-testid="nav-comparator">
                            Confronta Bolletta
                        </Link>
                    </nav>

                    {/* Actions */}
                    <div className="flex items-center gap-4">
                        <Link to="/prenota" data-testid="nav-book-repair">
                            <Button className="hidden sm:flex bg-primary hover:bg-primary-hover text-white">
                                <Wrench className="w-4 h-4 mr-2" />
                                Prenota Riparazione
                            </Button>
                        </Link>

                        {user && (
                            <Link to="/cart" className="relative p-2 hover:bg-surface-elevated rounded-lg transition-colors" data-testid="nav-cart">
                                <ShoppingCart className="w-5 h-5 text-white" />
                                {itemCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                                        {itemCount}
                                    </span>
                                )}
                            </Link>
                        )}

                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                                        <User className="w-5 h-5" />
                                        <span className="hidden sm:block">{user.name}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 bg-surface border-border">
                                    <DropdownMenuItem asChild>
                                        <Link to="/ordini" className="flex items-center gap-2" data-testid="menu-orders">
                                            <Package className="w-4 h-4" />
                                            I miei ordini
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/prenotazioni" className="flex items-center gap-2" data-testid="menu-bookings">
                                            <Wrench className="w-4 h-4" />
                                            Le mie prenotazioni
                                        </Link>
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin" className="flex items-center gap-2 text-primary" data-testid="menu-admin">
                                                    <LayoutDashboard className="w-4 h-4" />
                                                    Dashboard Admin
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-danger" data-testid="menu-logout">
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Esci
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/login" data-testid="nav-login">
                                <Button variant="outline" className="border-border hover:bg-surface-elevated">
                                    <User className="w-4 h-4 mr-2" />
                                    Accedi
                                </Button>
                            </Link>
                        )}

                        {/* Mobile menu button */}
                        <button
                            className="md:hidden p-2 hover:bg-surface-elevated rounded-lg"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <div className="md:hidden py-4 border-t border-border animate-fade-in">
                        <nav className="flex flex-col gap-2">
                            <Link to="/store" className="px-4 py-2 hover:bg-surface-elevated rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                                Store
                            </Link>
                            <Link to="/riparazioni" className="px-4 py-2 hover:bg-surface-elevated rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                                Riparazioni
                            </Link>
                            <Link to="/listino" className="px-4 py-2 hover:bg-surface-elevated rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                                Listino
                            </Link>
                            <Link to="/confronta" className="px-4 py-2 hover:bg-surface-elevated rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                                Confronta Bolletta
                            </Link>
                            <Link to="/prenota" className="px-4 py-2 hover:bg-surface-elevated rounded-lg text-primary" onClick={() => setMobileMenuOpen(false)}>
                                Prenota Riparazione
                            </Link>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
};

export default Header;
